import {
  AfterViewInit,
  Component,
  ElementRef,
  LOCALE_ID,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  untracked,
} from '@angular/core';

import { startOfDay } from 'date-fns';

import { CamRenderedContentDirective } from '../../directives/rendered-content.directive';
import { startOfWeekMonday, toDate } from '../../helpers/date';
import { groupBy } from '../../helpers/group';
import { CamLayoutSlot, computeLanes } from '../../helpers/layout';
import { CamEventRecord } from '../../models/event-record';
import { CamRecord } from '../../models/record';
import { CamSchedulerEngine, CamTimeAxis, CamWorkingTime } from '../../scheduler-engine';
import {
  CamColumnConfig,
  CamEventConfig,
  CamEventRenderer,
  CamResourceConfig,
  CamResourceTimeRangeConfig,
} from '../../types';

export type CamSchedulerHeaderCell = {
  key: string;
  label: string;
  width: number;
};

export type CamSchedulerBar = {
  color: string | null;
  content: HTMLElement | string | null;
  height: number;
  key: string;
  left: number;
  record: CamEventRecord;
  top: number;
  width: number;
};

export type CamSchedulerBand = {
  color: string | null;
  key: string;
  left: number;
  name: string;
  width: number;
};

export type CamSchedulerCell = {
  content: HTMLElement | string | null;
  key: string;
  width: number;
};

export type CamSchedulerRow = {
  bands: CamSchedulerBand[];
  bars: CamSchedulerBar[];
  cells: CamSchedulerCell[];
  height: number;
  key: string;
  resourceId: string;
};

type CamSchedulerDragState = {
  grabOffsetX: number;
  kind: 'move' | 'resize';
  originEnd: Date;
  originStart: Date;
  record: CamEventRecord;
};

const DEFAULT_ROW_HEIGHT = 60;
const BAR_PADDING = 2;

@Component({
  imports: [CamRenderedContentDirective],
  selector: 'cam-scheduler-widget',
  styleUrl: './scheduler-widget.component.scss',
  templateUrl: './scheduler-widget.component.html',
})
export class CamSchedulerWidgetComponent implements AfterViewInit, OnDestroy {
  columns = input<CamColumnConfig[]>([]);

  events = input<CamEventConfig[]>([]);

  resources = input<CamResourceConfig[]>([]);

  resourceTimeRanges = input<CamResourceTimeRangeConfig[]>([]);

  autoHeight = input(false);

  rowHeight = input(DEFAULT_ROW_HEIGHT);

  allowOverlap = input(true);

  workingTime = input<CamWorkingTime | null>(null);

  eventDragFeature = input(true);

  eventResizeFeature = input(true);

  eventRenderer = input<CamEventRenderer | null>(null);

  beforeDragMoveEnd = output<{ eventRecord: CamEventRecord; newEndDate: Date; newStartDate: Date }>();

  beforeDragResizeEnd = output<{ eventRecord: CamEventRecord; newEndDate: Date; newStartDate: Date }>();

  @ViewChild('timeline', { static: true })
  timeline!: ElementRef<HTMLDivElement>;

  readonly instance = new CamSchedulerEngine();

  readonly axis = computed<CamTimeAxis>(() => {
    this.instance.span();
    this.instance.preset();
    this.instance.workingTime();
    return this.instance.getAxis();
  });

  readonly lockedWidth = computed(() => this.columns().reduce((total, column) => total + (column.width ?? 150), 0));

  readonly headerGroups = computed<CamSchedulerHeaderCell[]>(() => {
    const axis = this.axis();
    const isHourly = this.instance.preset().base === 'hourAndDay';

    return axis.ticks.reduce<CamSchedulerHeaderCell[]>((groups, tick) => {
      const anchor = isHourly ? startOfDay(tick.start) : startOfWeekMonday(tick.start);
      const key = anchor.toISOString();
      const last = groups[groups.length - 1];

      if (last && last.key === key) {
        last.width += axis.tickWidth;
        return groups;
      }
      return [
        ...groups,
        {
          key,
          label: isHourly ? this._dayFormatter.format(anchor) : this._weekFormatter.format(anchor),
          width: axis.tickWidth,
        },
      ];
    }, []);
  });

  readonly headerTicks = computed<CamSchedulerHeaderCell[]>(() => {
    const axis = this.axis();
    const base = this.instance.preset().base;

    return axis.ticks.map(tick => ({
      key: tick.start.toISOString(),
      label: this._tickLabel(tick.start, base),
      width: axis.tickWidth,
    }));
  });

  readonly rows = computed<CamSchedulerRow[]>(() => {
    this.instance.revision();
    const axis = this.axis();
    const rowHeight = this.rowHeight();
    const eventsByResource = groupBy(this.instance.eventStore.records, record => String(record.getResourceId()));
    const bandsByResource = groupBy(this.instance.resourceTimeRangeStore.records, record =>
      String(record.getData('resourceId'))
    );

    return this.instance.resourceStore.records.map(resource => {
      const resourceId = String(resource.id);
      const records = eventsByResource.get(resourceId) ?? [];
      const layoutItems = records.map(record => ({
        end: record.getEndDate().getTime(),
        record,
        start: record.getStartDate().getTime(),
      }));
      const lanes: Map<(typeof layoutItems)[number], CamLayoutSlot> = this.allowOverlap()
        ? new Map()
        : computeLanes(layoutItems);
      const laneCount = Math.max(1, ...[...lanes.values()].map(slot => slot.lanes));

      return {
        bands: this._buildBands(resourceId, bandsByResource.get(resourceId) ?? [], axis),
        bars: layoutItems.map(item => {
          const slot = lanes.get(item) ?? { lane: 0, lanes: 1 };
          const left = axis.getXFromDate(item.record.getStartDate());
          return {
            color: this._resourceColor(item.record, resource),
            content: this._content(item.record, `${resourceId}-${item.record.id ?? item.start}`),
            height: rowHeight - BAR_PADDING * 2,
            key: `${resourceId}-${item.record.id ?? item.start}`,
            left,
            record: item.record,
            top: slot.lane * rowHeight + BAR_PADDING,
            width: Math.max(6, axis.getXFromDate(item.record.getEndDate()) - left),
          };
        }),
        cells: this.columns().map((column, index) => ({
          content: this._cellContent(column, resource, index),
          key: `${resourceId}-${index}`,
          width: column.width ?? 150,
        })),
        height: rowHeight * laneCount,
        key: resourceId,
        resourceId,
      };
    });
  });

  private readonly _locale = inject(LOCALE_ID);

  private readonly _dayFormatter = new Intl.DateTimeFormat(this._locale, {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  });
  private readonly _weekFormatter = new Intl.DateTimeFormat(this._locale, { day: 'numeric', month: 'long' });
  private readonly _hourFormatter = new Intl.DateTimeFormat(this._locale, { hour: '2-digit', minute: '2-digit' });
  private readonly _shortDayFormatter = new Intl.DateTimeFormat(this._locale, { day: 'numeric', weekday: 'short' });
  private readonly _letterFormatter = new Intl.DateTimeFormat(this._locale, { weekday: 'narrow' });

  private readonly _contentCache = new Map<string, HTMLElement | string>();
  private readonly _cellCache = new Map<string, HTMLElement | string>();

  private _dragState: CamSchedulerDragState | null = null;

  constructor() {
    effect(() => {
      const configs = this.events();
      untracked(() => {
        this._contentCache.clear();
        this.instance.eventStore.setRecords(configs.map(config => new CamEventRecord(config)));
      });
    });
    effect(() => {
      const configs = this.resources();
      untracked(() => {
        this._cellCache.clear();
        this.instance.resourceStore.setRecords(configs.map(config => new CamRecord(config)));
      });
    });
    effect(() => {
      const configs = this.resourceTimeRanges();
      untracked(() => this.instance.resourceTimeRangeStore.setRecords(configs.map(config => new CamRecord(config))));
    });
    effect(() => {
      const working = this.workingTime();
      untracked(() => this.instance.workingTime.set(working));
    });
  }

  ngAfterViewInit(): void {
    this.instance.timelineElement = this.timeline.nativeElement;
  }

  ngOnDestroy(): void {
    this._stopDrag();
  }

  public onBarPointerDown(event: PointerEvent, bar: CamSchedulerBar, kind: 'move' | 'resize'): void {
    if (event.button !== 0) {
      return;
    }
    if (kind === 'move' && !this.eventDragFeature()) {
      return;
    }
    if (kind === 'resize' && !this.eventResizeFeature()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this._dragState = {
      grabOffsetX: event.clientX - rect.left,
      kind,
      originEnd: bar.record.getEndDate(),
      originStart: bar.record.getStartDate(),
      record: bar.record,
    };
    document.addEventListener('pointermove', this._onDragMove);
    document.addEventListener('pointerup', this._onDragEnd);
  }

  private _tickLabel(date: Date, base: string | undefined): string {
    switch (base) {
      case 'dayAndWeek':
        return this._shortDayFormatter.format(date);
      case 'weekAndDayLetter':
        return this._letterFormatter.format(date);
      default:
        return this._hourFormatter.format(date);
    }
  }

  private _resourceColor(record: CamEventRecord, resource: CamRecord): string | null {
    return record.data['eventColor'] ?? resource.getData('eventColor') ?? null;
  }

  private _content(record: CamEventRecord, key: string): HTMLElement | string | null {
    const cached = this._contentCache.get(key);
    if (cached) {
      return cached;
    }
    const renderer = this.eventRenderer();
    if (!renderer) {
      return record.getName();
    }
    const rendered = renderer({ eventRecord: record, resourceRecord: null });
    this._contentCache.set(key, rendered);
    return rendered;
  }

  private _cellContent(column: CamColumnConfig, resource: CamRecord, index: number): HTMLElement | string | null {
    const key = `${resource.id}-${index}`;
    const cached = this._cellCache.get(key);
    if (cached) {
      return cached;
    }
    const value = column.field ? resource.getData(column.field) : null;
    if (!column.renderer) {
      return value ?? '';
    }
    const rendered = column.renderer({
      cellElement: document.createElement('div'),
      column,
      record: resource,
      value,
    });
    this._cellCache.set(key, rendered);
    return rendered;
  }

  private _buildBands(resourceId: string, records: CamRecord[], axis: CamTimeAxis): CamSchedulerBand[] {
    return records.flatMap((record, index) => {
      const start = toDate(record.getData('startDate'));
      const end = toDate(record.getData('endDate'));
      if (!start || !end) {
        return [];
      }
      const left = axis.getXFromDate(start);
      return [
        {
          color: record.getData('timeRangeColor') ?? record.getData('color') ?? null,
          key: `${resourceId}-${record.id ?? index}`,
          left,
          name: record.getData('name') ?? '',
          width: Math.max(2, axis.getXFromDate(end) - left),
        },
      ];
    });
  }

  private _onDragMove = (event: PointerEvent) => {
    const state = this._dragState;
    if (!state) {
      return;
    }
    const increment = this.instance.preset().timeResolution?.increment ?? 15;

    if (state.kind === 'resize') {
      const end = this.instance.getDateFromXY([event.clientX, event.clientY], 'round', false);
      const minEnd = new Date(state.originStart.getTime() + increment * 60 * 1000);
      state.record.setData({ endDate: end.getTime() < minEnd.getTime() ? minEnd : end });
      this.instance.refresh();
      return;
    }

    const duration = state.originEnd.getTime() - state.originStart.getTime();
    const start = this.instance.getDateFromXY([event.clientX - state.grabOffsetX, event.clientY], 'round', false);
    const resource = this.instance.resolveResourceRecord(event);
    state.record.setData({
      endDate: new Date(start.getTime() + duration),
      resourceId: resource.id ?? state.record.getResourceId(),
      startDate: start,
    });
    this.instance.refresh();
  };

  private _onDragEnd = () => {
    const state = this._dragState;
    this._stopDrag();

    if (!state) {
      return;
    }
    const payload = {
      eventRecord: state.record,
      newEndDate: state.record.getEndDate(),
      newStartDate: state.record.getStartDate(),
    };
    if (state.kind === 'resize') {
      this.beforeDragResizeEnd.emit(payload);
      return;
    }
    this.beforeDragMoveEnd.emit(payload);
  };

  private _stopDrag(): void {
    document.removeEventListener('pointermove', this._onDragMove);
    document.removeEventListener('pointerup', this._onDragEnd);
    this._dragState = null;
  }
}
